import React, { useEffect, useState } from "react";
import { Button, Flex, Form, Input, Typography, useMessagedAsyncFn } from "@payping/web-components";
import { FormInstance } from "@payping/web-components/components/Form/types";
import { apiConfigs, justAccessApis } from "@payping/api";
import { setTOTPCode } from "@payping/redux/actions";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { useAppDispatch, useAppSelector } from "@/utils/redux";
import { selectAtom } from "jotai/utils";
import { dashboardPreferencesAtom } from "@/core/state";
import { useAtom } from "jotai";
import { useImmerAtom } from "jotai-immer";

const otpLastTimeAtom = selectAtom(dashboardPreferencesAtom, (val) => val.user.otpLastTime);

interface Props {
  form: FormInstance<any>;
  onVerify?: (isVerified: boolean) => void;
}

export const OtpCode: React.FC<Props> = (props) => {
  const { t } = useAppTranslation();
  const { form, onVerify } = props;
  const dispatch = useAppDispatch();
  const [, setDashboardPreferences] = useImmerAtom(dashboardPreferencesAtom);

  const [otpLastTime] = useAtom(otpLastTimeAtom);
  const { TOTPCode } = useAppSelector((state) => state.auth);
  const [timer, setTimer] = useState<string | undefined>(undefined);
  const [isTimeExpired, setIsTimeExpired] = useState(false);
  const [isCodeSent, setIsCodeSent] = useState(false);

  useEffect(() => {
    apiConfigs.updateConfig({ totpCode: TOTPCode });
  }, [TOTPCode]);

  const getRemainingTime = () => {
    if (!otpLastTime) return undefined;

    const now = Date.now();
    const passed = Math.floor((now - Number(otpLastTime)) / 1000);
    const total = 300;

    const left = total - passed;

    if (left <= 0) {
      setIsTimeExpired(true);
      onVerify?.(false);
      form.resetFields(["otpCode"]);
      return undefined;
    }

    const minutes = Math.floor(left / 60);
    const seconds = left % 60;
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  useEffect(() => {
    if (otpLastTime) {
      const now = Date.now();
      const passed = Math.floor((now - Number(otpLastTime)) / 1000);
      const total = 300;
      const left = total - passed;
      if (left > 0) {
        setIsCodeSent(true);
      }
    }
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (isCodeSent && !isTimeExpired) {
      setTimer(getRemainingTime());
      interval = setInterval(() => {
        setTimer(getRemainingTime());
      }, 1000);
    }

    return () => {
      clearInterval(interval);
    };
  }, [isCodeSent, isTimeExpired, otpLastTime]);

  const sendCode = async () => {
    await justAccessApis.sendJitTokenToUserPost();
    setDashboardPreferences((draft) => {
      draft.user.otpLastTime = Date.now().toString();
    });
    setIsTimeExpired(false);
    setIsCodeSent(true);
  };

  const [, onSentCode] = useMessagedAsyncFn(sendCode, []);
  const [, resendCode] = useMessagedAsyncFn(async () => {
    await sendCode();
    setTimer(undefined);
  }, []);

  return (
    <Flex gap={"middle"} align={"center"} justify={"space-between"}>
      <Form.Item
        className="tw-w-full"
        rules={[
          { required: true },
          (form2) => ({
            validator: async (_, value) => {
              const otpCode = form2.getFieldValue("otpCode");
              if (!value || value.length !== 6) {
                return Promise.reject(new Error(t("theCodeEnteredIsNotCorrect")));
              }

              try {
                await justAccessApis.verifyJitTokenWithTokenPost(otpCode);
                apiConfigs.updateConfig({ totpCode: otpCode });
                await dispatch(setTOTPCode(otpCode));
                onVerify?.(true);
                return Promise.resolve();
              } catch (error) {
                onVerify?.(false);
                return Promise.reject(new Error(t("theCodeEnteredIsNotCorrect")));
              }
            },
          }),
        ]}
        validateTrigger={"onChange"}
        label={t("smsCode")}
        name={"otpCode"}
      >
        <Input.OTP
          maxLength={6}
          style={{ direction: "ltr", width: "100%" }}
          size={"large"}
          inputMode="numeric"
          autoFocus
          autoComplete="one-time-code"
        />
      </Form.Item>
      <div className="!tw-m-0">
        {!isCodeSent ? (
          <Button size="large" className="tw-flex tw-items-center tw-p-0" type="link" onClick={onSentCode}>
            {t("sendCode")}
          </Button>
        ) : isTimeExpired ? (
          <Button className="tw-flex tw-items-center !tw-p-0" size="small" type="link" onClick={resendCode}>
            {t("sendAgain")}
          </Button>
        ) : (
          <>
            <Typography.Text type="secondary" className="tw-whitespace-nowrap">
              {`${timer} ${t("sendAgain")} `}
            </Typography.Text>
          </>
        )}
      </div>
    </Flex>
  );
};
